import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatError, MatFormField, MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { map } from 'rxjs';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { Career, CreateCareer, CreateCareerStatusDynamicFields } from '../../../../api/app-types/career.app';
import {
	SocialSecurityStatus,
	SocialSecurityStatusActivity,
} from '../../../../api/app-types/social-security-status.app';
import { CAREER_API } from '../../../../api/services/career.service';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrSearchableSelect } from '../../../../core/components/searchable-select/searchable-select';
import { LvrSection } from '../../../../core/components/section/section';
import { LvrSectionForm } from '../../../../core/components/section/section-form';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { matDateProviders, updateYearsOfDate } from '../../../../shared/helpers/date.func';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { LOGGER } from '../../../../shared/logging/log.service';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { alreadyExistingValidator } from '../../../../shared/validators/existing-career-validator';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator, minDateValidator } from '../../../../shared/validators/max-date-validator';
import { SocialSecurityStatusFull } from './social-security-status-field-groups/social-security-status-full/social-security-status-full';
import { SocialSecurityStatusMinimal } from './social-security-status-field-groups/social-security-status-minimal/social-security-status-minimal';
import { SocialSecurityStatusSchool } from './social-security-status-field-groups/social-security-status-school/social-security-status-school';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-career-create',
	templateUrl: './career-create.html',
	styleUrl: './career-create.scss',
	imports: [
		LvrPaddingDirective,
		TranslocoPipe,
		ReactiveFormsModule,
		LvrField,
		MatFormField,
		LvrInputDirective,
		MatError,
		LvrAppendAsteriskPipe,
		MatDatepickerModule,
		MatNativeDateModule,
		MatInputModule,
		LvrSearchableSelect,
		SocialSecurityStatusFull,
		SocialSecurityStatusMinimal,
		SocialSecurityStatusSchool,
		MatTooltip,
		LvrRestoreActions,
		LvrAutoSaveDirective,
		LvrDefaultActions,
		LvrSection,
		LvrSectionForm,
	],
	providers: [...matDateProviders],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CareerCreate extends LvrRedirectNav {
	public readonly StatusActivity = SocialSecurityStatusActivity;
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly logger = inject(LOGGER);
	private readonly api = inject(CAREER_API);
	private readonly draftService = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly transloco = inject(TranslocoService);
	private readonly routeData = toSignal(inject(ActivatedRoute).data);
	private readonly socialSecurityStatusPage =
		this.resolveDataAsSignal<PaginatedResponse<SocialSecurityStatus>>('socialSecurityStatus');
	private readonly env = inject(ENVIRONMENT);
	private readonly clientId = this.resolveDataAsSignal<number>('clientId');
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly hasError = hasError;
	public readonly socialSecurityStatusOptions = computed(() => this.socialSecurityStatusPage()?.entities ?? []);
	private readonly careerPage = computed(() => this.routeData()?.['career'] as PaginatedResponse<Career>);
	private readonly existingDates = computed(
		() => this.careerPage()?.entities.map((date) => date.startOfCareerStage) ?? [],
	);

	public readonly draftId = FORM_KEYS.CREATE_CAREER;
	public readonly canCreateCareersPermission = hasPermission(this.session.permissions, 'Careers.Create');
	public readonly isLoading = signal<boolean>(false);

	// Configuration for the min and max date for the date picker
	public readonly minMaxDate = {
		minYears: this.env.formConfiguration.career.startOfCareerRange.min,
		maxYears: this.env.formConfiguration.career.startOfCareerRange.max,
		min: updateYearsOfDate(-1 * this.env.formConfiguration.career.startOfCareerRange.min),
		max: updateYearsOfDate(this.env.formConfiguration.career.startOfCareerRange.max),
	};

	private readonly fb = inject(FormBuilder);
	public readonly form = this.fb.group({
		date: [
			undefined,
			[
				Validators.required,
				minDateValidator(this.minMaxDate.min),
				maxDateValidator(this.minMaxDate.max),
				alreadyExistingValidator(this.existingDates()),
			],
		],
		remarkOfTheCareerStage: [''],
		socialSecurityStatusId: this.fb.control<undefined | number>(undefined, [Validators.required]),
		additionalFields: this.fb.control<CreateCareerStatusDynamicFields | undefined>(undefined, [Validators.required]),
	});

	private readonly socialSecurityStatusId = toSignal(
		this.form.controls['socialSecurityStatusId'].valueChanges.pipe(
			map(() => this.form.get('socialSecurityStatusId')!.value),
		),
	);
	public readonly selectedSocialSecurityStatus = computed(() => {
		const id = this.socialSecurityStatusId();
		return this.socialSecurityStatusOptions().find((status) => status.id === id);
	});

	public async save() {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.isLoading.set(true);

		try {
			const value = this.form.value;
			const body: CreateCareer = {
				socialSecurityStatusId: value.socialSecurityStatusId!,
				remark: value.additionalFields?.remark?.trim(),
				startOfCareerStage: value.date!,
				remarkOfTheCareerStage: value.remarkOfTheCareerStage?.trim(),
			};
			const isSocialSecurityStatusActivityLowerJ =
				this.selectedSocialSecurityStatus()?.statusActivity === SocialSecurityStatusActivity.j;
			if (
				isSocialSecurityStatusActivityLowerJ ||
				value.additionalFields?.branchId ||
				value.additionalFields?.schoolId
			) {
				body.dynamicFields = {
					branchId: value.additionalFields?.branchId || value.additionalFields?.schoolId,
					institutionInformed: value.additionalFields?.institutionInformed,
					jobTitle: value.additionalFields?.jobTitle?.trim(),
					personId: value.additionalFields?.personId,
					typeAndScopeOfTheWorkplace: value.additionalFields?.typeAndScopeOfTheWorkplace?.trim(),
					remark: value.additionalFields?.remark?.trim(),
					careerOrientationStartYear: value.additionalFields?.careerOrientationStartYear,
					cityDistrictId: value.additionalFields?.cityDistrictId,
				};
			}

			const clientId = this.clientId()!;
			await this.api.create(clientId, body);
			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.career_created')),
			);
			await this.navigateAfterFinished(['../'], { relativeTo: this.route });
		} catch (e) {
			this.logger.error(e as Error);
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.career_create_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	}

	public cancel() {
		this.draftService.deleteDraft('client', this.draftId);
		this.navigateAfterFinished(['../'], { relativeTo: this.route });
	}
}
